/* ============================================================================
   M5 — Courier Invoice Reconciliation  (route /supply/invoice-recon)
   Outer vertical tabs (D2C / PTL) → inner tabs (Dashboard · Invoices · Disputes).
   Upload invoice · raise/work disputes through the lifecycle · finance handoff.
   Replica runs as a ProShip Admin viewer (both verticals, all capabilities).
   ========================================================================== */
import React, {useState, useMemo} from 'react'
import PropTypes from 'prop-types'
import {useSearchParams} from 'react-router-dom'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import PageTitle from '../../../components/PageTitle'
import {PageHeader, SupplyTable, useToast, inr, fmtDateTime, download, toCsv} from '../components'
import {DUMMY_INVOICES, TODAY} from '../dummyData'
import {
  RECON,
  ReconBadge,
  VertChip,
  isDisputed,
  isClosed,
  monthsOf,
  couriersOf,
} from './reconShared'
import Tabs from './Tabs'
import DashboardTab from './DashboardTab'
import UploadInvoiceDrawer from './UploadInvoiceDrawer'
import DisputeDrawer from './DisputeDrawer'

const VERTICALS = ['D2C']
const IS_ADMIN = true

export default function InvoiceRecon() {
  const toast = useToast()
  const [params, setParams] = useSearchParams()

  const [tick, setTick] = useState(0) // forces re-derive after in-memory mutations
  const refresh = () => setTick((t) => t + 1)

  const outer = 'D2C'
  const inner =
    ['dashboard', 'invoices', 'disputes'].indexOf(params.get('tab')) >= 0
      ? params.get('tab')
      : 'dashboard'

  const [upload, setUpload] = useState(false)
  const [dispute, setDispute] = useState(null) // {invoice, raise}

  const setInner = (t) => {
    const next = new URLSearchParams(params)
    next.set('tab', t)
    setParams(next, {replace: true})
  }

  // rows scoped to the active outer vertical
  const rows = useMemo(
    () => DUMMY_INVOICES.filter((i) => i.vertical === outer),
    [outer, tick] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const openDispute = (invoice, raise) => setDispute({invoice, raise: !!raise})

  return (
    <>
      <PageTitle>{'Courier Invoice Reconciliation'}</PageTitle>

      <PageHeader
        title='Courier Invoice Reconciliation'
        sub='Reconcile courier invoices, track disputes & credit notes, and hand off to finance. Showing D2C data.'
        actions={
          <button className='btn btn-primary' onClick={() => setUpload(true)}>
            <FileUploadOutlinedIcon style={{width: 16, height: 16}} />
            Upload Courier Invoice
          </button>
        }
      />

      {/* inner tabs */}
      <Tabs
        tabs={[
          {id: 'dashboard', label: 'Dashboard'},
          {id: 'invoices', label: 'Invoices'},
          {id: 'disputes', label: 'Disputes'},
        ]}
        value={inner}
        onChange={setInner}
      />

      {inner === 'dashboard' ? <DashboardTab key={outer} invoices={rows} /> : null}

      {inner === 'invoices' ? <InvoicesTab rows={rows} onOpen={openDispute} toast={toast} /> : null}

      {inner === 'disputes' ? <DisputesTab rows={rows} onOpen={openDispute} toast={toast} /> : null}

      <UploadInvoiceDrawer
        open={upload}
        verticals={VERTICALS}
        onClose={() => setUpload(false)}
        onUploaded={refresh}
        toast={toast}
      />

      <DisputeDrawer
        invoice={dispute ? dispute.invoice : null}
        raise={dispute ? dispute.raise : false}
        isAdmin={IS_ADMIN}
        onClose={() => setDispute(null)}
        onSaved={refresh}
        toast={toast}
      />
    </>
  )
}

/* ── INVOICES tab ── */
function InvoicesTab({rows, onOpen, toast}) {
  const months = monthsOf(rows)
  const couriers = couriersOf(rows)
  const columns = [
    {
      key: 'id',
      label: 'Invoice #',
      render: (i) => <span className='mono'>{i.id}</span>,
    },
    {
      key: 'partner',
      label: 'Partner',
      render: (i) => (
        <span>
          <b>{i.partner}</b> <VertChip vertical={i.vertical} />
        </span>
      ),
      exportValue: (i) => i.partner,
    },
    {
      key: 'period',
      label: 'Period',
      render: (i) => <span style={{whiteSpace: 'nowrap'}}>{i.period}</span>,
    },
    {
      key: 'orders',
      label: 'Orders',
      align: 'right',
      render: (i) => Number(i.orders).toLocaleString('en-IN'),
    },
    {
      key: 'billedAmount',
      label: 'Billed',
      align: 'right',
      value: (i) => i.billedAmount,
      render: (i) => <b>{inr(i.billedAmount)}</b>,
    },
    {
      key: 'expectedAmount',
      label: 'Expected',
      align: 'right',
      value: (i) => i.expectedAmount,
      render: (i) => inr(i.expectedAmount),
    },
    {
      key: 'variance',
      label: 'Variance',
      align: 'right',
      value: (i) => i.variance,
      render: (i) =>
        i.variance > 0 ? (
          <span style={{color: 'var(--error)', fontWeight: 600}}>+{inr(i.variance)}</span>
        ) : (
          <span className='muted'>—</span>
        ),
    },
    {
      key: 'cnAmount',
      label: 'CN',
      align: 'right',
      value: (i) => i.cnAmount,
      render: (i) =>
        i.cnAmount ? (
          <span style={{color: '#0F766E', fontWeight: 600}}>{inr(i.cnAmount)}</span>
        ) : (
          <span className='muted'>—</span>
        ),
    },
    {
      key: 'reconStatus',
      label: 'Recon Status',
      render: (i) => <ReconBadge status={i.reconStatus} />,
    },
    {
      key: 'createdBy',
      label: 'Created By',
      render: (i) => <span style={{fontSize: 12}}>{i.createdBy || '—'}</span>,
    },
    {
      key: 'createdAt',
      label: 'Created At',
      value: (i) => i.createdAt,
      render: (i) => (
        <span style={{whiteSpace: 'nowrap', fontSize: 12}}>{fmtDateTime(i.createdAt)}</span>
      ),
    },
  ]

  const filters = [
    {
      key: 'reconStatus',
      label: 'Recon Status',
      options: RECON.map((r) => ({value: r.k, label: r.k})),
      predicate: (i, sel) => sel.has(i.reconStatus),
    },
    {
      key: 'monthKey',
      label: 'Month',
      options: months.map((m) => ({value: m.k, label: m.label})),
      predicate: (i, sel) => sel.has(i.monthKey),
    },
    {
      key: 'partner',
      label: 'Partner',
      options: couriers.map((c) => ({value: c, label: c})),
      predicate: (i, sel) => sel.has(i.partner),
    },
    {
      key: 'billedAmount',
      label: 'Total amount range',
      type: 'range',
      field: 'billedAmount',
      unit: '₹',
    },
  ]

  return (
    <SupplyTable
      columns={columns}
      rows={rows}
      rowKey={(i) => i.id}
      search={(r, q) =>
        [r.id, r.partner, r.reason, r.remark, String(r.billedAmount)].some(
          (v) =>
            String(v == null ? '' : v)
              .toLowerCase()
              .indexOf(q) >= 0
        )
      }
      searchPlaceholder='Search invoice #, partner, reason, amount...'
      defaultSort={{key: 'createdAt', dir: 'desc'}}
      pageSize={10}
      exportName='courier-invoices'
      emptyText='No invoices uploaded yet.'
      filters={filters}
      rowClick={(i) => onOpen(i)}
      rowActions={(i) => [
        {
          label: 'View / reconcile',
          icon: <VisibilityOutlinedIcon fontSize='small' />,
          onClick: () => onOpen(i),
        },
        {
          label: 'Download invoice',
          icon: <DownloadOutlinedIcon fontSize='small' />,
          onClick: () => toast('Downloading ' + i.id + ' (mock).', 'info'),
        },
        i.reconStatus === 'No Dispute' ? {sep: true} : null,
        i.reconStatus === 'No Dispute'
          ? {
              label: 'Raise dispute',
              icon: <WarningAmberOutlinedIcon fontSize='small' />,
              danger: true,
              onClick: () => onOpen(i, true),
            }
          : null,
      ]}
    />
  )
}

/* ── DISPUTES tab ── */
function DisputesTab({rows, onOpen, toast}) {
  const couriers = couriersOf(rows)
  const reasons = Array.from(
    new Set(
      DUMMY_INVOICES.filter(isDisputed)
        .map((i) => i.reason)
        .filter(Boolean)
    )
  )
  const disputeRows = rows.filter(isDisputed)

  const exportHandoff = () => {
    const closed = rows.filter(isClosed)
    if (!closed.length) {
      toast('No closed disputes to hand off yet.', 'info')
      return
    }
    const csv = toCsv(closed, [
      {label: 'Invoice', value: (i) => i.id},
      {label: 'Partner', value: (i) => i.partner},
      {label: 'Vertical', value: (i) => i.vertical},
      {label: 'Period', value: (i) => i.period},
      {label: 'Billed', value: (i) => i.billedAmount},
      {label: 'Disputed', value: (i) => i.disputedAmount},
      {label: 'Final CN', value: (i) => i.cnAmount},
      {label: 'Net Payable', value: (i) => i.billedAmount - (i.cnAmount || 0)},
      {label: 'Reason', value: (i) => i.reason},
      {label: 'Resolution Days', value: (i) => i.resolutionDays},
    ])
    download('cn-finance-handoff_' + TODAY.toISOString().slice(0, 10) + '.csv', csv)
    toast('Finance handoff exported (' + closed.length + ' closed disputes with CN).', 'success')
  }

  const columns = [
    {
      key: 'id',
      label: 'Invoice #',
      render: (i) => <span className='mono'>{i.id}</span>,
    },
    {
      key: 'partner',
      label: 'Partner',
      render: (i) => (
        <span>
          <b>{i.partner}</b> <VertChip vertical={i.vertical} />
        </span>
      ),
      exportValue: (i) => i.partner,
    },
    {
      key: 'period',
      label: 'Period',
      render: (i) => <span style={{whiteSpace: 'nowrap'}}>{i.period}</span>,
    },
    {
      key: 'disputedAmount',
      label: 'Disputed',
      align: 'right',
      value: (i) => i.disputedAmount,
      render: (i) => <b style={{color: 'var(--error)'}}>{inr(i.disputedAmount)}</b>,
    },
    {
      key: 'cnAmount',
      label: 'Final CN',
      align: 'right',
      value: (i) => i.cnAmount,
      render: (i) =>
        i.cnAmount ? (
          <span style={{color: '#0F766E', fontWeight: 600}}>{inr(i.cnAmount)}</span>
        ) : (
          <span className='muted'>—</span>
        ),
    },
    {key: 'reason', label: 'Reason', render: (i) => <span className='tag'>{i.reason || '—'}</span>},
    {key: 'reconStatus', label: 'Status', render: (i) => <ReconBadge status={i.reconStatus} />},
    {
      key: 'owner',
      label: 'Owner',
      render: (i) => <span style={{fontSize: 12}}>{i.owner || '—'}</span>,
    },
    {
      key: 'age',
      label: 'Age / Resolution',
      align: 'center',
      sortable: false,
      render: (i) => {
        if (i.resolutionDays != null)
          return <span className='tag'>{i.resolutionDays}d to resolve</span>
        if (!i.raisedDate) return <span className='muted'>—</span>
        const age = Math.floor((TODAY - new Date(i.raisedDate)) / 86400000)
        return (
          <span className={'mini-pill ' + (age > 14 ? 'mp-red' : 'mp-amber')}>{age}d open</span>
        )
      },
    },
  ]

  const filters = [
    {
      key: 'reconStatus',
      label: 'Status',
      options: RECON.filter((r) => r.k !== 'No Dispute').map((r) => ({value: r.k, label: r.k})),
      predicate: (i, sel) => sel.has(i.reconStatus),
    },
    {
      key: 'reason',
      label: 'Reason',
      options: reasons.map((r) => ({value: r, label: r})),
      predicate: (i, sel) => sel.has(i.reason),
    },
    {
      key: 'partner',
      label: 'Partner',
      options: couriers.map((c) => ({value: c, label: c})),
      predicate: (i, sel) => sel.has(i.partner),
    },
  ]

  return (
    <>
      <div className='page-head' style={{marginBottom: 12}}>
        <div>
          <div className='seclabel' style={{marginBottom: 4}}>
            Invoice Disputes Queue
          </div>
          <p className='sub' style={{margin: 0}}>
            Lifecycle: Open — Courier End / Prozo End → CN Pending → Closed &amp; Shared with
            Finance (or Rejected). Closing &amp; finance handoff is ProShip-Admin only.
          </p>
        </div>
        <button className='btn' style={{marginLeft: 'auto'}} onClick={exportHandoff}>
          <DownloadOutlinedIcon style={{width: 16, height: 16}} />
          Export Finance Handoff
        </button>
      </div>

      <SupplyTable
        columns={columns}
        rows={disputeRows}
        rowKey={(i) => i.id}
        search={(r, q) =>
          [r.id, r.partner, r.reason, r.owner].some(
            (v) =>
              String(v == null ? '' : v)
                .toLowerCase()
                .indexOf(q) >= 0
          )
        }
        searchPlaceholder='Search invoice #, partner, reason, owner...'
        defaultSort={{key: 'disputedAmount', dir: 'desc'}}
        exportName='invoice-disputes'
        emptyText='No disputes raised. Raise one from the Invoices tab.'
        filters={filters}
        rowClick={(i) => onOpen(i)}
        rowActions={(i) => [
          {
            label: 'View / manage',
            icon: <VisibilityOutlinedIcon fontSize='small' />,
            onClick: () => onOpen(i),
          },
          !isClosed(i) && i.reconStatus !== 'Rejected'
            ? {
                label: 'Update status',
                icon: <SyncOutlinedIcon fontSize='small' />,
                onClick: () => onOpen(i),
              }
            : null,
        ]}
      />
    </>
  )
}

InvoicesTab.propTypes = {
  rows: PropTypes.array,
  onOpen: PropTypes.func,
  toast: PropTypes.func,
}
DisputesTab.propTypes = {
  rows: PropTypes.array,
  onOpen: PropTypes.func,
  toast: PropTypes.func,
}
